// packages/core/src/learning/schools/crypto-web3.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no figures; Satoshi
// Nakamoto and Vitalik Buterin appear as canon authors only.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const CRYPTO_WEB3_EXHIBIT: SchoolExhibitEntry = {
	id: 'crypto-web3',
	tagline: 'Crypto and web3 believes money can move without old gatekeepers.',
	worldviewSummary:
		'Crypto and web3 is paying attention to decentralization, digital ownership, custody, and new rails for money and coordination. This lens protects you from assuming banks, platforms, governments, and brokers are the only possible way financial systems can work. It accepts a serious tradeoff: more control can also mean more responsibility, more volatility, more scams, and fewer safety nets. The blind spot is belief turning into speculation. Tokens can lose value, projects can fail, custody mistakes can be permanent, and outcomes are not reliable.',
	coreBeliefs: [
		'Crypto and web3 believes trust can sometimes be moved from institutions into code, networks, and public rules.',
		'Crypto and web3 believes ownership matters more when platforms can change terms, freeze access, or control the rails.',
		'Crypto and web3 believes open networks may create new ways to move value, build communities, and coordinate online.',
		'Crypto and web3 believes custody is power, but custody also creates personal responsibility and real risk.',
		'Crypto and web3 believes volatility is not a side detail. It is part of the environment.',
		'Crypto and web3 believes tokens can be useful, but token prices can also become speculation with possible losses.',
		'Crypto and web3 believes the technology thesis and the investment outcome are not the same thing.',
	],
	warnsAgainst: [
		'Assuming the old financial system is the only possible design.',
		'Confusing decentralization language with safety.',
		'Believing a token must rise because the technology sounds important.',
		'Ignoring scams, hacks, custody mistakes, regulation risk, and total loss risk.',
	],
	helps: 'This lens helps people who want to understand why some users care about digital ownership, open networks, custody, and alternatives to centralized platforms. It may not fit someone who needs stability, simple investing, low volatility, strong consumer protections, or a low attention money system.',
	figures: [],
	canon: [
		{
			title: 'Bitcoin: A Peer to Peer Electronic Cash System',
			author: 'Satoshi Nakamoto',
			oneLineIdea:
				'The original crypto idea was not just a coin. It was a proposal for value transfer without a central operator.',
		},
		{
			title: 'Ethereum white paper',
			author: 'Vitalik Buterin',
			oneLineIdea:
				'Ethereum expanded the lens from digital money to programmable agreements and network based applications.',
		},
		{
			title: 'Self custody',
			oneLineIdea:
				'Control can move closer to the user, but mistakes, lost keys, and bad security can become permanent losses.',
		},
		{
			title: 'Decentralized finance',
			oneLineIdea:
				'Financial functions can be rebuilt on open networks, but smart contract risk and incentive risk still matter.',
		},
		{
			title: 'Token incentives',
			oneLineIdea:
				'Tokens can coordinate behavior, but they can also turn real communities into short term price games.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-crypto-web3',
	debateIds: [],
	applyActions: [
		{
			label: 'Check whether your money life can absorb high volatility',
			action: { type: 'calculateRunway', label: 'Check your runway' },
		},
		{
			label: 'Compare a high risk idea against your real numbers',
			action: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
		},
		{
			label: 'Test how a speculative outcome changes your plan',
			action: {
				type: 'openSimulator',
				label: 'Simulate a risk case',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Write the risk before the upside story',
			action: {
				type: 'noToolAction',
				label: 'Write the risk case',
				prompt: 'Write how a crypto idea could fail before writing how it could work.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Crypto is not just a price chart. It is a fight over who controls the rails.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of self custody is that no one may save you from your own mistake.',
			kind: 'hook',
		},
		{
			line: 'What did crypto get right about trust, and what did the hype get dangerously wrong?',
			kind: 'interview-question',
		},
		{
			line: 'Have you ever seen people confuse a technology thesis with a token price bet?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk teaches crypto as a worldview, not as a promise that a token will win.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Users should be able to study new money rails without being recruited into speculation.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience the difference between believing in open networks and betting on a token.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask people what they think ownership means when a platform controls the account.',
			kind: 'creator-prompt',
		},
		{
			line: 'Walk through a custody mistake and explain why control can become responsibility fast.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
